import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { castList, episodePosters, episodes, recommendations } from '@/mocks/siteData';
import DemoPlayer from './components/DemoPlayer';
import EpisodeCard from '@/components/feature/EpisodeCard';
import RecommendCard from '@/components/feature/RecommendCard';
import Breadcrumb from '@/components/feature/Breadcrumb';

const FAV_KEY = 'shan_de_xiao_yi_zi_favorites';

export default function PlayPage() {
  const { episode: episodeParam } = useParams();
  const episodeNum = Number(episodeParam);
  const current = episodes.find((ep) => ep.episode === episodeNum);

  const [favorites, setFavorites] = useState<number[]>([]);
  const [toast, setToast] = useState('');

  useEffect(() => {
    try {
      const raw = localStorage.getItem(FAV_KEY);
      if (raw) {
        const parsed = JSON.parse(raw);
        if (Array.isArray(parsed)) setFavorites(parsed);
      }
    } catch {
      setFavorites([]);
    }
  }, []);

  useEffect(() => {
    if (current) {
      document.title = `《善良的小姨子》第${current.episode}集在线观看（演示）`;
      const desc = document.querySelector('meta[name="description"]');
      if (desc) {
        desc.setAttribute(
          'content',
          `《善良的小姨子》第${current.episode}集剧情介绍与在线观看演示，${current.summary}`
        );
      }
    }
  }, [current]);

  useEffect(() => {
    if (!toast) return;
    const t = window.setTimeout(() => setToast(''), 2000);
    return () => window.clearTimeout(t);
  }, [toast]);

  const isFavorite = current ? favorites.includes(current.episode) : false;

  const toggleFavorite = useCallback(() => {
    if (!current) return;
    setFavorites((prev) => {
      const next = prev.includes(current.episode)
        ? prev.filter((e) => e !== current.episode)
        : [...prev, current.episode];
      try {
        localStorage.setItem(FAV_KEY, JSON.stringify(next));
      } catch {
        // ignore storage errors
      }
      setToast(next.includes(current.episode) ? '已加入收藏' : '已取消收藏');
      return next;
    });
  }, [current]);

  const relatedEpisodes = useMemo(() => {
    if (!current) return [];
    const others = episodes.filter((ep) => ep.episode !== current.episode);
    const start = (current.episode - 1) % others.length;
    return [...others.slice(start), ...others.slice(0, start)].slice(0, 3);
  }, [current]);

  const episodeCast = useMemo(() => {
    if (!current) return [];
    return castList.filter((c) => c.episodes.includes(current.episode));
  }, [current]);

  if (!current) {
    return (
      <div className="w-full px-4 md:px-6 py-20 text-center">
        <span className="inline-flex w-16 h-16 items-center justify-center rounded-full bg-background-100 text-foreground-400 mb-4">
          <i className="ri-file-warning-line text-3xl"></i>
        </span>
        <h1 className="font-heading font-semibold text-foreground-950 text-xl mb-2">
          未找到该剧集信息
        </h1>
        <p className="text-sm text-foreground-500 mb-6">
          本剧共 {episodes.length} 集，请返回剧情列表重新选择。
        </p>
        <Link
          to="/category/plot"
          className="inline-flex items-center gap-2 px-5 py-2.5 rounded-md bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-colors cursor-pointer"
        >
          <i className="ri-arrow-left-line w-4 h-4 flex items-center justify-center"></i>
          返回剧情列表
        </Link>
      </div>
    );
  }

  const poster = episodePosters[(current.episode - 1) % episodePosters.length];
  const hasPrev = current.episode > 1;
  const hasNext = current.episode < episodes.length;

  return (
    <div className="w-full px-4 md:px-6 py-6 md:py-10 animate-fade-up">
      <Breadcrumb items={[{ label: '剧情介绍', to: '/category/plot' }, { label: `第${current.episode}集` }]} />

      <div className="mt-6 grid grid-cols-1 lg:grid-cols-3 gap-8">
        <div className="lg:col-span-2 min-w-0">
          <DemoPlayer poster={poster} title={current.title} episode={current.episode} />

          <div className="mt-6 flex flex-col sm:flex-row sm:items-start justify-between gap-4">
            <div>
              <h1 className="font-heading font-bold text-foreground-950 text-2xl md:text-3xl mb-2">
                第{current.episode}集 · {current.title}
              </h1>
              <div className="flex flex-wrap items-center gap-4 text-sm text-foreground-500">
                <span className="flex items-center gap-1">
                  <i className="ri-calendar-line w-4 h-4 flex items-center justify-center"></i>
                  播出日期：{current.airDate}
                </span>
                <span className="flex items-center gap-1">
                  <i className="ri-time-line w-4 h-4 flex items-center justify-center"></i>
                  {current.duration}
                </span>
                <span className="px-2 py-0.5 rounded bg-secondary-100 text-secondary-900 text-xs">
                  全 {episodes.length} 集
                </span>
              </div>
            </div>
            <button
              type="button"
              onClick={toggleFavorite}
              className={`inline-flex items-center gap-2 px-5 py-2.5 rounded-md text-sm font-medium whitespace-nowrap transition-colors cursor-pointer shrink-0 ${
                isFavorite
                  ? 'bg-accent-500 text-primary-950 hover:bg-accent-400'
                  : 'border border-background-300 text-foreground-800 hover:border-accent-400 hover:text-accent-700'
              }`}
            >
              <i className={isFavorite ? 'ri-heart-fill w-4 h-4 flex items-center justify-center' : 'ri-heart-line w-4 h-4 flex items-center justify-center'}></i>
              {isFavorite ? '已收藏' : '收藏本集'}
            </button>
          </div>

          <div className="mt-6 bg-background-50 border border-background-200 rounded-lg p-6">
            <h2 className="font-heading font-semibold text-foreground-950 text-lg mb-3 flex items-center gap-2">
              <i className="ri-file-text-line w-5 h-5 flex items-center justify-center text-primary-600"></i>
              剧情介绍
            </h2>
            <p className="text-sm md:text-base text-foreground-700 leading-loose">{current.plot}</p>
          </div>

          {episodeCast.length > 0 && (
            <div className="mt-6 bg-background-50 border border-background-200 rounded-lg p-6">
              <h2 className="font-heading font-semibold text-foreground-950 text-lg mb-4 flex items-center gap-2">
                <i className="ri-team-line w-5 h-5 flex items-center justify-center text-primary-600"></i>
                本集出场演员
              </h2>
              <div className="flex flex-wrap gap-3">
                {episodeCast.map((c) => (
                  <Link
                    key={c.id}
                    to={`/detail/cast/${c.id}`}
                    className="flex items-center gap-2 pl-1 pr-3.5 py-1 rounded-full bg-background-100 border border-background-200 hover:border-accent-400 transition-colors"
                  >
                    <span className="w-8 h-8 rounded-full overflow-hidden bg-background-200 shrink-0">
                      <img
                        src={c.avatar}
                        alt={`${c.name} 饰演 ${c.role}`}
                        title={`${c.name} - 善良的小姨子角色详情`}
                        className="w-full h-full object-cover object-top"
                      />
                    </span>
                    <span className="text-sm text-foreground-800">{c.name}</span>
                  </Link>
                ))}
              </div>
            </div>
          )}

          <div className="mt-6 flex items-center justify-between gap-3">
            {hasPrev ? (
              <Link
                to={`/play/${current.episode - 1}`}
                className="flex-1 flex items-center gap-2 px-4 py-3 rounded-md border border-background-300 text-foreground-800 hover:border-accent-400 hover:text-accent-700 transition-colors"
              >
                <i className="ri-arrow-left-line w-4 h-4 flex items-center justify-center shrink-0"></i>
                <span className="min-w-0">
                  <span className="block text-xs text-foreground-500">上一集</span>
                  <span className="block text-sm truncate">第{current.episode - 1}集</span>
                </span>
              </Link>
            ) : (
              <span className="flex-1" />
            )}
            {hasNext ? (
              <Link
                to={`/play/${current.episode + 1}`}
                className="flex-1 flex items-center justify-end gap-2 px-4 py-3 rounded-md border border-background-300 text-foreground-800 hover:border-accent-400 hover:text-accent-700 transition-colors text-right"
              >
                <span className="min-w-0">
                  <span className="block text-xs text-foreground-500">下一集</span>
                  <span className="block text-sm truncate">第{current.episode + 1}集</span>
                </span>
                <i className="ri-arrow-right-line w-4 h-4 flex items-center justify-center shrink-0"></i>
              </Link>
            ) : (
              <span className="flex-1" />
            )}
          </div>
        </div>

        <aside className="w-full lg:w-auto space-y-5">
          <div className="bg-background-50 border border-background-200 rounded-lg p-5">
            <h2 className="font-heading font-semibold text-foreground-950 text-base mb-4 flex items-center gap-2">
              <i className="ri-history-line w-5 h-5 flex items-center justify-center text-primary-600"></i>
              同剧推荐
            </h2>
            <ul className="space-y-3">
              {relatedEpisodes.map((ep) => (
                <li key={ep.episode}>
                  <Link
                    to={`/play/${ep.episode}`}
                    className="flex items-center gap-3 group"
                  >
                    <span className="w-12 h-12 rounded-md overflow-hidden bg-background-100 shrink-0">
                      <img
                        src={episodePosters[(ep.episode - 1) % episodePosters.length]}
                        alt={`第${ep.episode}集 ${ep.title}`}
                        title={`第${ep.episode}集 - 善良的小姨子`}
                        className="w-full h-full object-cover object-top"
                      />
                    </span>
                    <span className="min-w-0">
                      <span className="block text-sm text-foreground-800 group-hover:text-accent-700 transition-colors truncate">
                        第{ep.episode}集 · {ep.title}
                      </span>
                      <span className="block text-xs text-foreground-500 line-clamp-1">
                        {ep.summary}
                      </span>
                    </span>
                  </Link>
                </li>
              ))}
            </ul>
            <Link
              to="/category/plot"
              className="mt-4 w-full inline-flex items-center justify-center gap-1 px-4 py-2.5 rounded-md border border-background-300 text-foreground-800 text-sm font-medium hover:border-accent-400 hover:text-accent-700 transition-colors whitespace-nowrap cursor-pointer"
            >
              全部剧集
              <i className="ri-arrow-right-line w-4 h-4 flex items-center justify-center"></i>
            </Link>
          </div>

          <div className="bg-accent-50 border border-accent-200 rounded-lg p-5">
            <h2 className="font-heading font-semibold text-accent-900 text-base mb-2 flex items-center gap-2">
              <i className="ri-heart-3-line w-5 h-5 flex items-center justify-center"></i>
              收藏提示
            </h2>
            <p className="text-xs text-accent-800 leading-relaxed">
              收藏记录保存在本地浏览器中，仅用于演示。当前已收藏 {favorites.length} 集。
            </p>
          </div>
        </aside>
      </div>

      <section className="mt-12">
        <div className="flex items-center gap-3 mb-6">
          <span className="w-1 h-6 bg-accent-500 rounded-full"></span>
          <h2 className="font-heading font-bold text-foreground-950 text-xl md:text-2xl">
            看完这集，你可能还想看
          </h2>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5">
          {recommendations.slice(0, 3).map((item, idx) => (
            <RecommendCard key={item.id} item={item} index={idx} />
          ))}
        </div>
      </section>

      {toast && (
        <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-50 px-5 py-3 rounded-md bg-primary-900 text-background-50 text-sm shadow-lg animate-slide-fade">
          {toast}
        </div>
      )}
    </div>
  );
}